// browser-encrypt-aes-cbc.js
const encoder = new TextEncoder();
const decoder = new TextDecoder();

// IV must match Node's iv: bytes [0..15]
const IV_BYTES = new Uint8Array([0,1,2,3,4,5,6,7,8,9,10,11,12,13,14,15]);

function bufToHex(buf) {
  const bytes = new Uint8Array(buf);
  return Array.from(bytes).map(b => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Derive raw 32-byte key using PBKDF2(SHA-512) so it matches Node's pbkdf2Sync(..., 32, 'sha512')
 * @param {string} password
 * @param {string} salt
 * @param {number} iterations (65536)
 * @returns {Promise<ArrayBuffer>} 32-byte derived key
 */
async function deriveRawKey(password, salt, iterations = 65536) {
  const pwBuf = encoder.encode(password); // UTF-8
  const saltBuf = encoder.encode(salt);   // UTF-8

  const baseKey = await crypto.subtle.importKey(
    'raw',
    pwBuf,
    'PBKDF2',
    false,
    ['deriveBits']
  );

  // derive 256 bits (32 bytes)
  const derivedBits = await crypto.subtle.deriveBits(
    {
      name: 'PBKDF2',
      salt: saltBuf,
      iterations,
      hash: 'SHA-512'
    },
    baseKey,
    256 // bits
  );

  return derivedBits; // ArrayBuffer (32 bytes)
}

/**
 * Encrypt plaintext (utf8) using AES-256-CBC with key derived from password+salt (PBKDF2-SHA512).
 * Returns hex string (same format as Node's Buffer(...).toString('hex')).
 *
 * @param {string} plaintext
 * @param {string} password
 * @param {string} salt
 * @returns {Promise<string>} hex ciphertext
 */
export async function encryptAes256CbcToHex(plaintext, password, salt) {
  const rawKey = await deriveRawKey(password, salt, 65536); // ArrayBuffer of 32 bytes

  // import raw key as AES-CBC key
  const cryptoKey = await crypto.subtle.importKey(
    'raw',
    rawKey,
    { name: 'AES-CBC' },
    false,
    ['encrypt']
  );

  const ctBuffer = await crypto.subtle.encrypt(
    { name: 'AES-CBC', iv: IV_BYTES },
    cryptoKey,
    encoder.encode(plaintext) // plaintext -> Uint8Array
  );

  return bufToHex(ctBuffer); // hex string
}

/* Example usage (in React or console):
(async () => {
  const plaintext = 'Hello secure world!';
  const password = config.req_enc_key; // same string used on Node
  const salt = config.req_salt;        // same string on Node
  const hex = await encryptAes256CbcToHex(plaintext, password, salt);
  console.log('ciphertext hex:', hex);
})();
*/
